import React, { useState, useEffect } from 'react';
import { 
    TextField, 
    Button, 
    Typography, 
    Paper, 
    Container, 
    MenuItem, 
    CircularProgress,
    Chip,
    Box,
    Snackbar,
    Alert
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createTale, updateTale } from '../../actions/tales';
import { getGroups } from '../../actions/groups';
import { useAuth } from '../../contexts/AuthContext';
import { Filter } from 'bad-words';
import { getErrorMessage } from '../../utils/errorHandler';

const Form = () => {
    const location = useLocation();
    const isEditing = location.state?.isEditing || false;
    const existingTale = location.state?.taleData;

    const [taleData, setTaleData] = useState({
        title: '',
        story: '',
        group: '',
        tags: []
    });
    const [tagInput, setTagInput] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const filter = new Filter();
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success' // 'error', 'warning', 'info', 'success'
    });

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const groups = useSelector((state) => state.groups);
    const { currentUser, isAuthenticated } = useAuth();

    useEffect(() => {
        if (isEditing && existingTale) {
            setTaleData({
                title: existingTale.title,
                story: existingTale.story,
                group: existingTale.group,
                tags: existingTale.tags || []
            });
        }
    }, [isEditing, existingTale]);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/auth');
        }
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        const init = async () => {
            if (!currentUser) {
                navigate('/auth');
                return;
            }
            
            try {
                await dispatch(getGroups());
                setIsLoading(false);
            } catch (error) {
                setIsLoading(false);
            }
        };

        init();
    }, [currentUser, navigate, dispatch]);

    const checkProfanity = (text) => {
        try {
            return filter.isProfane(text);
        } catch (error) {
            return false;
        }
    };

    const validateInputs = () => {
        const newErrors = {};
        
        if (!taleData.title.trim()) {
            newErrors.title = 'Title is required';
        } else if (taleData.title.length < 3) {
            newErrors.title = 'Title must be at least 3 characters long';
        } else if (checkProfanity(taleData.title)) {
            newErrors.title = 'Please remove inappropriate language from the title';
        }

        if (!taleData.story.trim()) {
            newErrors.story = 'Story is required';
        } else if (taleData.story.length < 10) {
            newErrors.story = 'Story must be at least 10 characters long';
        } else if (checkProfanity(taleData.story)) {
            newErrors.story = 'Please remove inappropriate language from the story';
        }

        if (!taleData.group) {
            newErrors.group = 'Please select a group';
        }

        if (taleData.tags.some(tag => checkProfanity(tag))) {
            newErrors.tags = 'Please remove inappropriate language from tags';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateInputs()) {
            showNotification('Please fix the form errors before submitting.', 'error');
            return;
        }

        setSubmitting(true);
        try {
            const selectedGroup = groups.find(g => g.name === taleData.group);
            const groupColor = selectedGroup?.color || '#3498db';

            const talePayload = {
                ...taleData,
                user: currentUser._id,
                groupColor,
                createdDate: new Date().toISOString()
            };

            if (isEditing) {
                await dispatch(updateTale(existingTale.id, talePayload));
                showNotification('Your story has been updated successfully!');
            } else {
                await dispatch(createTale(talePayload));
                showNotification('Your story has been shared successfully!');
            }

            clear();
            navigate('/tales');
        } catch (error) {
            showNotification(getErrorMessage(error), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddTag = (e) => {
        if (e.key === 'Enter' && tagInput.trim()) {
            e.preventDefault();
            const newTag = tagInput.trim();
            
            if (checkProfanity(newTag)) {
                setErrors({ ...errors, tags: 'Please remove inappropriate language from tags' });
                return;
            }

            if (!taleData.tags.includes(newTag)) {
                setTaleData({
                    ...taleData,
                    tags: [...taleData.tags, newTag]
                });
                setErrors({ ...errors, tags: undefined });
            }
            setTagInput('');
        }
    };

    const handleDeleteTag = (tagToDelete) => {
        setTaleData({
            ...taleData,
            tags: taleData.tags.filter((tag) => tag !== tagToDelete)
        });
    };

    const clear = () => {
        setTaleData({
            title: '',
            story: '',
            group: '',
            tags: []
        });
        setTagInput('');
    };

    const handleCloseNotification = () => {
        setNotification(prev => ({ ...prev, open: false }));
    };

    const showNotification = (message, severity = 'success') => {
        setNotification({
            open: true,
            message,
            severity
        });
    };

    if (isLoading) {
        return (
            <Container sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                minHeight: 'calc(100vh - 64px)'
            }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container 
            maxWidth="md" 
            sx={{ 
                mt: 4, 
                mb: 4
            }}
        >
            <Paper 
                elevation={2}
                sx={{
                    padding: { xs: 2, sm: 4 },
                    borderRadius: 2,
                    backgroundColor: '#ffffff'
                }}
            >
                <form 
                    autoComplete="off" 
                    noValidate 
                    onSubmit={handleSubmit}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px'
                    }}
                >
                    <Typography 
                        variant="h5"
                        sx={{
                            marginBottom: 2,
                            color: '#2d3748',
                            fontWeight: 500
                        }}
                    >
                        {isEditing ? 'Edit Your Story' : 'Share Your Story'}
                    </Typography>

                    <TextField
                        name="title"
                        variant="outlined"
                        label="Title"
                        fullWidth
                        value={taleData.title}
                        onChange={(e) => setTaleData({ ...taleData, title: e.target.value })}
                        error={!!errors.title}
                        helperText={errors.title}
                    />

                    <TextField
                        name="story"
                        variant="outlined"
                        label="Your Story"
                        fullWidth
                        multiline
                        rows={6}
                        value={taleData.story}
                        onChange={(e) => setTaleData({ ...taleData, story: e.target.value })}
                        error={!!errors.story}
                        helperText={errors.story}
                    />

                    <TextField
                        name="group"
                        variant="outlined"
                        label="Select Group"
                        fullWidth
                        select
                        value={taleData.group}
                        onChange={(e) => setTaleData({ ...taleData, group: e.target.value })}
                        error={!!errors.group}
                        helperText={errors.group}
                    >
                        {groups.map((group) => (
                            <MenuItem key={group._id} value={group.name}>
                                {group.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    <div>
                        <TextField
                            name="tags"
                            variant="outlined"
                            label="Add Tags"
                            fullWidth
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyPress={handleAddTag}
                            helperText={errors.tags || "Press Enter to add tags"}
                            error={!!errors.tags}
                            sx={{ marginBottom: 1 }}
                        />
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                flexWrap: 'wrap', 
                                gap: 1,
                                minHeight: '32px'
                            }}
                        >
                            {taleData.tags.map((tag) => (
                                <Chip
                                    key={tag}
                                    label={tag}
                                    onDelete={() => handleDeleteTag(tag)}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                />
                            ))}
                        </Box>
                    </div>

                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={submitting}
                            sx={{
                                py: 1.5,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                                }
                            }}
                        >
                            {submitting ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                isEditing ? 'Update Story' : 'Share Your Story'
                            )}
                        </Button>
                        <Button
                            onClick={clear}
                            variant="outlined"
                            color="secondary"
                            fullWidth
                            disabled={submitting}
                            sx={{ py: 1.5 }}
                        >
                            Clear
                        </Button>
                    </Box>
                </form>
            </Paper>

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseNotification}
                    severity={notification.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Form;